import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useState, useEffect } from "react";
import { ProjectDetailsDialog } from "./ProjectDetailsDialog";
import { User } from "lucide-react";

export const ProjectKanbanBoard = ({ complexityFilter }: { complexityFilter: "all" | "low" | "medium" | "high" }) => {
  const { currentOrganization } = useOrganization();
  const queryClient = useQueryClient();
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  // Fetch users (project leaders)
  const { data: users } = useQuery({
    queryKey: ["profiles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, email")
        .order("full_name");
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch projects with team members
  const { data: projects, isLoading } = useQuery({
    queryKey: ["kanban-projects", currentOrganization?.id],
    queryFn: async () => {
      if (!currentOrganization) throw new Error("No organization selected");

      const { data, error } = await supabase
        .from("projects")
        .select(`
          *,
          project_team_members (
            user_id,
            profiles:user_id (
              id,
              full_name,
              email
            )
          ),
          project_lead:project_lead_id (
            id,
            full_name,
            email
          )
        `)
        .eq("organization_id", currentOrganization.id)
        .in("status", ["Not Started", "In Progress"])
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!currentOrganization,
  });

  // Set up real-time subscription
  useEffect(() => {
    if (!currentOrganization) return;

    const channel = supabase
      .channel("kanban-projects-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "projects",
          filter: `organization_id=eq.${currentOrganization.id}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["kanban-projects", currentOrganization.id] });
        }
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "project_team_members",
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["kanban-projects", currentOrganization.id] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentOrganization, queryClient]);

  const handleProjectClick = (project: any) => {
    setSelectedProject(project);
    setIsDetailsOpen(true);
  };

  // Filter projects by complexity
  const filteredProjects = projects?.filter((project) => {
    if (complexityFilter === "all") return true;
    return project.complexity === complexityFilter;
  });

  // Group projects by project leader
  const projectsByLeader = filteredProjects?.reduce((acc: any, project: any) => {
    const leaderId = project.project_lead_id || "unassigned";
    if (!acc[leaderId]) {
      acc[leaderId] = [];
    }
    acc[leaderId].push(project);
    return acc;
  }, {});

  // Get only leaders who have projects assigned
  const leadersWithProjects = Object.keys(projectsByLeader || {}).map((leaderId) => {
    if (leaderId === "unassigned") {
      return { id: "unassigned", full_name: "Sin Asignar", email: null };
    }
    return users?.find((u) => u.id === leaderId) || null;
  }).filter(Boolean);

  const getComplexityColor = (complexity: string) => {
    switch (complexity) {
      case "low":
        return "bg-green-500/10 text-green-500";
      case "high":
        return "bg-red-500/10 text-red-500";
      default:
        return "bg-yellow-500/10 text-yellow-500";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Not Started":
        return "bg-gray-500/10 text-gray-500";
      case "In Progress":
        return "bg-blue-500/10 text-blue-500";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <>
      <div className="flex gap-4 overflow-x-auto pb-4">
        {leadersWithProjects.map((leader: any) => {
          const leaderProjects = projectsByLeader?.[leader.id] || [];
          
          return (
            <div key={leader.id} className="flex-shrink-0 w-80">
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback>
                      {leader.id === "unassigned" ? (
                        <User className="h-4 w-4" />
                      ) : (
                        leader.full_name?.charAt(0).toUpperCase() || "?"
                      )}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{leader.full_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {leaderProjects.length} {leaderProjects.length === 1 ? "proyecto" : "proyectos"}
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  {leaderProjects.map((project: any) => (
                    <Card
                      key={project.id}
                      className="cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => handleProjectClick(project)}
                    >
                      <CardContent className="p-4">
                        <h3 className="font-medium text-sm mb-2 line-clamp-2">
                          {project.name}
                        </h3>

                        <div className="flex flex-wrap gap-1 mb-3">
                          <Badge
                            variant="outline"
                            className={getStatusColor(project.status)}
                          >
                            {project.status}
                          </Badge>
                          {project.complexity && (
                            <Badge
                              variant="outline"
                              className={getComplexityColor(project.complexity)}
                            >
                              {project.complexity === "low"
                                ? "Baja"
                                : project.complexity === "high"
                                ? "Alta"
                                : "Media"}
                            </Badge>
                          )}
                        </div>

                        {/* Show project lead and team members */}
                        <div className="flex items-center gap-2 mb-2">
                          {project.project_lead && (
                            <div className="flex items-center gap-1">
                              <Avatar className="h-6 w-6 border-2 border-primary">
                                <AvatarFallback className="text-xs bg-primary/10">
                                  {project.project_lead.full_name?.charAt(0).toUpperCase() || "?"}
                                </AvatarFallback>
                              </Avatar>
                              <span className="text-xs font-medium text-primary">Líder</span>
                            </div>
                          )}
                        </div>

                        {project.project_team_members &&
                          project.project_team_members.length > 0 && (
                            <div className="flex items-center gap-1">
                              <p className="text-xs text-muted-foreground mr-1">Equipo:</p>
                              <div className="flex -space-x-2">
                                {project.project_team_members.slice(0, 3).map((member: any) => (
                                  <Avatar key={member.user_id} className="h-6 w-6 border-2 border-background">
                                    <AvatarFallback className="text-xs">
                                      {member.profiles?.full_name?.charAt(0).toUpperCase() || "?"}
                                    </AvatarFallback>
                                  </Avatar>
                                ))}
                                {project.project_team_members.length > 3 && (
                                  <div className="h-6 w-6 rounded-full bg-muted border-2 border-background flex items-center justify-center text-xs">
                                    +{project.project_team_members.length - 3}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                      </CardContent>
                    </Card>
                  ))}

                  {leaderProjects.length === 0 && (
                    <div className="text-center text-sm text-muted-foreground py-8">
                      Sin proyectos activos
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <ProjectDetailsDialog
        project={selectedProject}
        open={isDetailsOpen}
        onOpenChange={setIsDetailsOpen}
      />
    </>
  );
};
